import axios from 'axios';

// Use VITE_API_URL for consistency with other services
// Default to empty string (relative URLs) for production - nginx proxies /auth/ to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface AuthResponse {
  success: boolean;
  token?: string;
  error?: string;
}

export interface User {
  username: string;
  token: string;
}

class AuthService {
  private static instance: AuthService;
  private user: User | null = null;
  private token: string | null = null;

  private constructor() {
    // Check for existing token in localStorage
    const savedToken = localStorage.getItem('authToken');
    const savedUsername = localStorage.getItem('username');

    if (savedToken && savedUsername) {
      this.token = savedToken;
      this.user = { username: savedUsername, token: savedToken };
      this.setAuthHeader(savedToken);
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private setAuthHeader(token: string) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  private clearAuthHeader() {
    delete axios.defaults.headers.common['Authorization'];
  }

  public async login(username: string, password: string): Promise<boolean> {
    try {
      // Call the backend's auth endpoint which will handle the NomnomData authentication
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      });

      if (response.data.success && response.data.token) {
        this.token = response.data.token;
        this.user = { username, token: response.data.token };

        // Store in localStorage
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('username', username);

        // Set auth header for future requests
        this.setAuthHeader(response.data.token);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  public logout(): void {
    this.token = null;
    this.user = null;

    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');

    // Clear auth header
    this.clearAuthHeader();
  }

  public isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  public getUser(): User | null {
    return this.user;
  }

  public getToken(): string | null {
    return this.token;
  }

  public async validateToken(): Promise<boolean> {
    if (!this.token) return false;

    try {
      // Validate token with backend
      const response = await axios.get(`${API_BASE_URL}/auth/validate`, {
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      return response.data.valid === true;
    } catch (error) {
      console.error('Token validation failed:', error);
      this.logout(); // Clear invalid token
      return false;
    }
  }
}

export default AuthService;
